import Select from '@/components/Select';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as SelectPrimitive from '@radix-ui/react-select';
import { findConverter, getShape, getValueShapeOf, ShapeName } from '@squaredup/data-streams';
import { capitalize, upperFirst } from 'lodash';
import { metadataEditorShapes } from '../MetadataEditorShapes';
import { CloneStateColumn, ComparisonStateColumn, ComputedStateColumn, StateColumn } from '../MetadataEditorState';
import { MetadataTableRow } from '../MetadataEditorTable';

const getShapeOptionGroups = (shapeName?: ShapeName) => {
    const valueShapeName = shapeName ? getValueShapeOf(shapeName)?.name : shapeName;

    const optionGroups = metadataEditorShapes.map((group) => ({
        label: group.groupName,
        options: group.shapes
            .filter((s) => {
                if (valueShapeName == null) {
                    return false;
                }

                const targetValueShapeName = s.isPrimitive || !s.isTotal ? s.name : s.valueShape.name;

                const requiresConversion = valueShapeName !== targetValueShapeName;
                const hasConverter = findConverter(valueShapeName, targetValueShapeName) != null;

                return !requiresConversion || hasConverter;
            })
            .map((s) => ({
                label: upperFirst(s.displayName),
                value: s.name,
                disabled: false
            }))
    }));

    const selectedShapeIsValid =
        shapeName == null || optionGroups.some((g) => g.options.some((o) => o.value === shapeName));

    if (!selectedShapeIsValid) {
        const shape = getShape(shapeName);

        if (shape != null) {
            // Selected shape is not one you can normally pick in the metadata editor
            // (e.g. a legacy shape like number.0), so add a disabled option to avoid
            // the internal shape name being displayed to the user
            optionGroups.push({
                label: 'Unsupported',
                options: [{ disabled: true, label: shape.displayName, value: shape.name }]
            });
        }
    }

    return optionGroups;
};

export const ShapeDropdown: React.FC<{
    row: MetadataTableRow;
    columnShape: ShapeName;
    columns: (StateColumn | CloneStateColumn | ComparisonStateColumn | ComputedStateColumn)[];
    onChange: (value: ShapeName) => void;
}> = ({ row, columns, columnShape, onChange }) => {
    const column = columns.find((c) => c.columnName === row.name);

    const disableShapeDropdown =
        column?.isComparison &&
        column?.metadataOverrides &&
        'comparisonType' in column.metadataOverrides &&
        column.metadataOverrides.comparisonType === 'percentage';

    return (
        <Select
            name='shape'
            disabled={disableShapeDropdown}
            value={columnShape}
            onValueChange={(value: ShapeName) => onChange(value)}
        >
            <Select.Trigger
                className='w-full border-none ring-inset ring-1 ring-outlinePrimary text-textPrimary rounded-input leading-input focus:outline-none focus-visible:ring-inset focus-visible:ring-1 py-input px-md bg-componentBackgroundPrimary data-[state="open"]:ring-outlineSecondary focus:ring-outlineSecondary hover:ring-outlineSecondary placeholder:text-textIncomplete disabled:text-textDisabled disabled:pointer-events-none'
                data-testid='metadataShapingTrigger'
            >
                <span className='flex items-center justify-between gap-2'>
                    <span>{capitalize(getShape(columnShape)?.displayName ?? columnShape)}</span>
                    <SelectPrimitive.Icon asChild>
                        <FontAwesomeIcon icon={faChevronDown} />
                    </SelectPrimitive.Icon>
                </span>
            </Select.Trigger>

            <Select.Content
                position='popper'
                className='overflow-auto text-base max-h-[--radix-select-content-available-height]'
                avoidCollisions
                sideOffset={3}
            >
                {getShapeOptionGroups(row.shape as ShapeName)
                    .filter((group) => Boolean(group.options.length))
                    .map((group) => (
                        <Select.Group key={group.label}>
                            <Select.Label className='text-xs font-medium uppercase pl-xs text-textIncomplete'>
                                {group.label}
                            </Select.Label>

                            {group.options.map((option) => (
                                <Select.Item
                                    key={option.value}
                                    value={option.value}
                                    className='data-[state="checked"]:bg-dividerTertiary cursor-pointer mx-0'
                                >
                                    {option.label}
                                </Select.Item>
                            ))}
                        </Select.Group>
                    ))}
            </Select.Content>
        </Select>
    );
};
